import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSidebarVisibility } from '../../contexts/SidebarVisibilityContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SidebarTree from '../../components/tree/SidebarTree';
import { useBureauCategory } from '../../hooks/useBureauCategory';
import { useAlarmSound } from '../../hooks/useAlarmSound';
import { useAlarmSoundContext } from '../../contexts/AlarmSoundContext';
import { getSite } from '../../services/siteManagement/siteManagement.service';
import { alarmAcknowledgedEvent } from '../../services/events/alarmEvents';
import { useAuth0 } from '../../contexts/useAuthContext';
import { useBureauSignalRContext } from '../../contexts/BureauSignalRContext';
import { fetchCshGroups, fetchCshGroup, fetchCshSidebarGroups } from '../../services/csh/csh.service';
import type { CshGroupSummary, CshGroupDetail, CshSidebarGroup } from '../../services/csh/csh.types';

import { useTreeRefresh } from '../../contexts/TreeRefreshContext';
import type { BureauAlarm, AnyObject } from './types/sidebar.types';
import useBureauCustomersTree from './hooks/useBureauCustomersTree';


interface SidebarProps { collapsed?: boolean }

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const { treeNodes: nodes, treeLoading, treeError, bureauMode, cshRefreshKey } = useTreeRefresh();
  const location = useLocation();
  const { isNormalUser, isAdmin, isSupport } = useAuth0();
  const { subscribe } = useBureauSignalRContext();

  // Auto-collapse sidebar when viewport is below MD breakpoint
  const MD_BREAKPOINT = 768; // px
  const [isCollapsedByBreakpoint, setIsCollapsedByBreakpoint] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' && window.innerWidth < MD_BREAKPOINT;
    } catch (e) { return false; }
  });

  useEffect(() => {
    try {
      if (typeof window === 'undefined') {return;}
      const mq = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
      const handler = (ev: MediaQueryListEvent | MediaQueryList) => setIsCollapsedByBreakpoint((ev as MediaQueryListEvent).matches === true);
      // set initial
      setIsCollapsedByBreakpoint(mq.matches === true);
      if (mq.addEventListener) {mq.addEventListener('change', handler as EventListener);}
      else {mq.addListener(handler as EventListener);}
      return () => {
        try { if (mq.removeEventListener) {mq.removeEventListener('change', handler as EventListener);} else {mq.removeListener(handler as EventListener);} } catch (e) { /* ignore */ }
      };
    } catch (e) { /* ignore */ }
  }, []);

  // Re-enrich CSH details when the main site tree refreshes (so added/removed VTs are picked up)
  useEffect(() => {
    let mounted = true;
    if (!cshDetails || Object.keys(cshDetails).length === 0) {return;}
    (async () => {
      try {
        const detailMap: Record<number, CshGroupDetail | null> = { ...cshDetails };
        const enrichPromises: Array<Promise<void>> = [];
        Object.keys(detailMap).forEach((gid) => {
          const det = detailMap[Number(gid)];
          if (!det || !Array.isArray(det.sites)) {return;}
            det.sites.forEach((s: AnyObject, idx: number) => {
              const siteRegistryId = (s as AnyObject).siteIdentifierRegistryId ?? (s as AnyObject).siteRegistryId ?? (s as AnyObject).sirId ?? null;
            if (!siteRegistryId) {return;}
            enrichPromises.push((async () => {
              try {
                  const res = await getSite(Number(siteRegistryId)).catch(() => ({ success: false } as unknown as { success: boolean }));
                  if (res && res.success && res.data && Array.isArray((res.data as AnyObject).children)) {
                    const children = ((res.data as AnyObject).children as AnyObject[]);
                    const vts = children.filter((c: AnyObject) => (c as AnyObject).entityType === 'VirtualTenant' || (c as AnyObject).isVirtualTenant === true);
                  if (vts && vts.length > 0) {
                      (det.sites as AnyObject[])[idx] = { ...(det.sites as AnyObject[])[idx], virtualTenants: vts } as AnyObject;
                  } else {
                    // remove any previously attached virtualTenants if none present now
                      const existing = (det.sites as AnyObject[])[idx];
                      if (existing && (existing as AnyObject).virtualTenants) {
                        const copy = { ...existing } as AnyObject;
                        delete (copy as AnyObject).virtualTenants;
                        (det.sites as AnyObject[])[idx] = copy as AnyObject;
                    }
                  }
                }
              } catch (e) { /* ignore per-site enrich errors */ }
            })());
          });
        });
        await Promise.allSettled(enrichPromises);
        if (mounted) {setCshDetails(detailMap);}
      } catch (e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [nodes, cshRefreshKey]);

  const displayCollapsed = collapsed || isCollapsedByBreakpoint;

  // When on homepage OR in bureau mode, fetch bureau category results so we can limit the sidebar to matching sites
  const isSitePage = location.pathname.startsWith('/sites/') || location.pathname.startsWith('/virtualTenants/');
  const isCustomerPage = location.pathname.startsWith('/customers/');
  
  // Controlled selection id for the trees (shared) so we can clear selection on navigation
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Clear selection when navigating away from any site/VT/customer page
  useEffect(() => {
    if (!isSitePage && !isCustomerPage) {setSelectedId(null);}
  }, [isSitePage, isCustomerPage]);
  
  // Bureau mode is now always enabled (Layout.tsx)
  const shouldFilterForBureau = bureauMode;
  // Always fetch bureau data, but only use it when in bureau mode
  const { data: bureauData = [], allData: bureauAllData, loading: bureauLoading, refresh: refreshBureauData } = useBureauCategory('all', 100, false);
  
  // Fetch bureau customers tree with pre-calculated alarm counts for CSH Groups
  const { 
    nodes: cshGroupsNodes, 
    unacknowledgedCounts: cshGroupsUnacknowledgedCounts, 
    loading: cshGroupsLoading, 
    error: cshGroupsError,
    refresh: refreshCshGroupsTree 
  } = useBureauCustomersTree();
  
  // Track expanded state for CSH Groups tree - persists across data refreshes
  const [cshGroupsExpandedKeys, setCshGroupsExpandedKeys] = useState<Set<string>>(new Set());
  
  // Helper to determine whether an alarm should be considered acknowledged by the UI
  const isAlarmAcknowledged = useCallback((alarm: BureauAlarm) => {
    // Check if alarm is acknowledged - the backend returns localAck: true for acknowledged alarms
    return alarm.localAck === true;
  }, []);
  
  // Build a stable memoized set of identifiers from the bureau payload: siteIdentifier, siteGuid, siteName, siteHierarchyId, sirId
  // IMPORTANT: Only include sites with UNACKNOWLEDGED alarms so the tree filtering works correctly
  const allowedSiteIdentifiers = useMemo(() => {
    // Prefer the authoritative full merged set when available (bureauAllData),
    // otherwise fall back to the paged `bureauData`.
    const source = Array.isArray(bureauAllData) && bureauAllData.length > 0 ? bureauAllData : bureauData;
    if (!shouldFilterForBureau || !Array.isArray(source)) {return undefined;}
    const keys = new Set<string>();
    source.forEach((b: BureauAlarm) => {
      // Skip acknowledged alarms - only include unacknowledged alarms
      if (isAlarmAcknowledged(b)) {return;}

      const pick = (v: unknown) => {
        if (v === undefined || v === null) {return;}
        const s = String(v).trim();
        if (s) {keys.add(s.toLowerCase());}
      };
      pick(b.siteIdentifier);
      pick(b.siteGuid);
      pick(b.siteName);
      pick(b.siteHierarchyId);
      pick(b.sirId);
      pick(b.shId);
      // also check nested site objects
      if (b.site && typeof b.site === 'object') {
        pick(b.site.siteIdentifier);
        pick(b.site.siteGuid);
        pick(b.site.siteName);
        pick(b.site.siteHierarchyId);
      }
    });
    return keys.size > 0 ? Array.from(keys) : undefined;
  }, [shouldFilterForBureau, bureauData, bureauAllData, isAlarmAcknowledged]);

  // No debug logging in Sidebar
  useEffect(() => { /* no-op; intentionally left blank to avoid console noise */ }, [shouldFilterForBureau, allowedSiteIdentifiers, nodes]);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const adminButtonRef = useRef<HTMLButtonElement>(null);
  const usersButtonRef = useRef<HTMLButtonElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const accessButtonRef = useRef<HTMLButtonElement>(null);
  
  // Admin tooltip state
  const [showAdminTooltip, setShowAdminTooltip] = useState<boolean>(false);
  const [adminTooltipPosition, setAdminTooltipPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0, left: 0 });
  
  // Users button tooltip state
  const [showUsersTooltip, setShowUsersTooltip] = useState<boolean>(false);
  const [usersTooltipPosition, setUsersTooltipPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0, left: 0 });
  
  // Help button tooltip state
  const [showHelpTooltip, setShowHelpTooltip] = useState<boolean>(false);
  const [helpTooltipPosition, setHelpTooltipPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0, left: 0 });
  
  // Request Access button tooltip state
  const [showAccessTooltip, setShowAccessTooltip] = useState<boolean>(false);
  const [accessTooltipPosition, setAccessTooltipPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0, left: 0 });

  // CSH Groups uses bureau customers tree directly - no complex mapping needed
  const cshGroupsMappingInProgress = shouldFilterForBureau && cshGroupsLoading;

  // Admin tooltip handlers
  const handleAdminMouseEnter = useCallback(() => {
    if (displayCollapsed && adminButtonRef.current) {
      const rect = adminButtonRef.current.getBoundingClientRect();
      setAdminTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8
      });
      setShowAdminTooltip(true);
    }
  }, [displayCollapsed]);

  // Users button tooltip handlers
  const handleUsersMouseEnter = useCallback(() => {
    if (displayCollapsed && usersButtonRef.current) {
      const rect = usersButtonRef.current.getBoundingClientRect();
      const isRTL = document.body.classList.contains('right-left-layout');
      
      if (isRTL) {
        setUsersTooltipPosition({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 8
        });
      } else {
        setUsersTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 8
        });
      }
      setShowUsersTooltip(true);
    }
  }, [displayCollapsed]);

  const handleUsersMouseLeave = useCallback(() => {
    setShowUsersTooltip(false);
  }, []);

  // Help button tooltip handlers
  const handleHelpMouseEnter = useCallback(() => {
    if (displayCollapsed && helpButtonRef.current) {
      const rect = helpButtonRef.current.getBoundingClientRect();
      const isRTL = document.body.classList.contains('right-left-layout');
      
      if (isRTL) {
        setHelpTooltipPosition({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 8
        });
      } else {
        setHelpTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 8
        });
      }
      setShowHelpTooltip(true);
    }
  }, [displayCollapsed]);

  const handleHelpMouseLeave = useCallback(() => {
    setShowHelpTooltip(false);
  }, []);

  // Request Access button tooltip handlers
  const handleAccessMouseEnter = useCallback(() => {
    if (displayCollapsed && accessButtonRef.current) {
      const rect = accessButtonRef.current.getBoundingClientRect();
      const isRTL = document.body.classList.contains('right-left-layout');
      
      if (isRTL) {
        setAccessTooltipPosition({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 8
        });
      } else {
        setAccessTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 8
        });
      }
      setShowAccessTooltip(true);
    }
  }, [displayCollapsed]);

  const handleAccessMouseLeave = useCallback(() => {
    setShowAccessTooltip(false);
  }, []);

  const unacknowledgedCounts = useMemo(() => {
    // Use authoritative full merged alarms when available to compute badge counts.
    const sourceArray = Array.isArray(bureauAllData) && bureauAllData.length > 0 ? bureauAllData : bureauData;
    // Only count ACTIVE unacknowledged alarms (not inactive ones)
    const source = Array.isArray(sourceArray) ? sourceArray.filter((alarm: BureauAlarm) => !isAlarmAcknowledged(alarm) && alarm.bureauAlarmState === 'ActiveAlarmsNotAck') : [];
    if (!source || source.length === 0) {return undefined;}

    // Deduplicate alarms by unique id/identifier to avoid counting duplicates
    const seen = new Set<string|number>();
    const uniqueAlarms = source.filter((alarm: BureauAlarm) => {
      const uid = alarm.bureauAlarmId ?? alarm.id ?? alarm.alarmIdentifier ?? `${alarm.siteGuid || ''}-${alarm.lastOccurrenceAt || ''}`;
      if (seen.has(uid)) {return false;}
      seen.add(uid);
      return true;
    });

    // Map site identifiers to customer keys and site keys so we can count per customer and per-site
    const siteIdToCustomerKey: Record<string, string> = {};
    const siteGuidToCustomerKey: Record<string, string> = {};
    const siteIdentifierToCustomerKey: Record<string, string> = {};
    // Also build reverse maps to get the site node key (e.g. 'site-123') for site-level badges
    const siteIdToSiteKey: Record<string, string> = {};
    const siteGuidToSiteKey: Record<string, string> = {};
    const siteIdentifierToSiteKey: Record<string, string> = {};

    // Build mapping from nodes: only top-level children are sites (customer -> site)
    try {
        if (Array.isArray(nodes)) {
        nodes.forEach((cust: AnyObject) => {
        const custKey = String((cust as AnyObject).key);
        const children = Array.isArray((cust as AnyObject).children) ? ((cust as AnyObject).children as AnyObject[]) : [];
        children.forEach((site: AnyObject) => {
          const siteKey = String((site as AnyObject).key);
          const sid = (site as AnyObject).sirId ?? (site as AnyObject).siteRegistryId ?? null;
          const sg = (site as AnyObject).siteGuid ?? null;
          const si = (site as AnyObject).siteIdentifier ?? null;
          if (sid) {
            siteIdToCustomerKey[String(sid)] = custKey;
            siteIdToSiteKey[String(sid)] = siteKey;
          }
          if (sg) {
            siteGuidToCustomerKey[String(sg).toLowerCase()] = custKey;
            siteGuidToSiteKey[String(sg).toLowerCase()] = siteKey;
          }
          if (si) {
            siteIdentifierToCustomerKey[String(si).toLowerCase()] = custKey;
            siteIdentifierToSiteKey[String(si).toLowerCase()] = siteKey;
          }
          // Also include per siteKey mapping for exact lookup (map siteKey -> siteKey for convenience)
          if (siteKey) {siteIdToSiteKey[siteKey] = siteKey;}
          if (siteKey) {siteIdToCustomerKey[siteKey] = custKey;}
        });
        });
      }
    } catch (e) { /* ignore mapping errors */ }

    const counts: Record<string, number> = {};
    const unmatched: AnyObject[] = [];
    uniqueAlarms.forEach((alarm: BureauAlarm) => {
      let mappedCust: string | undefined;
      let mappedSiteKey: string | undefined;
      try {
        const alarmSiteId = alarm.siteRegistryId ?? alarm.sirId ?? alarm.siteId ?? alarm.id ?? null;
        const alarmGuid = alarm.siteGuid ? String(alarm.siteGuid).toLowerCase() : null;
        const alarmIdentifier = alarm.siteIdentifier ? String(alarm.siteIdentifier).toLowerCase() : null;
        // Check by numeric siteId
        if (alarmSiteId != null) {
          mappedCust = siteIdToCustomerKey[String(alarmSiteId)];
          mappedSiteKey = siteIdToSiteKey[String(alarmSiteId)];
        }
        // Fallback to GUID
        if (!mappedCust && alarmGuid) {
          mappedCust = siteGuidToCustomerKey[alarmGuid];
        }
        if (!mappedSiteKey && alarmGuid) {mappedSiteKey = siteGuidToSiteKey[alarmGuid];}
        // Fallback to siteIdentifier
        if (!mappedCust && alarmIdentifier) {mappedCust = siteIdentifierToCustomerKey[alarmIdentifier];}
        if (!mappedSiteKey && alarmIdentifier) {mappedSiteKey = siteIdentifierToSiteKey[alarmIdentifier];}
      } catch (err) { /* ignore per-alarm mapping errors */ }

      if (mappedCust) {
        counts[mappedCust] = (counts[mappedCust] || 0) + 1;
      }

      // Also increment per-site counts where we can determine the site node key
      if (mappedSiteKey) {
        counts[mappedSiteKey] = (counts[mappedSiteKey] || 0) + 1;
      } else {
        unmatched.push(alarm);
      }
    });

    // debug logging intentionally removed to reduce console noise

    return Object.keys(counts).length > 0 ? counts : undefined;
  }, [bureauData, bureauAllData, nodes, isAlarmAcknowledged]);

  // Helpers to map main tree nodes to find matching keys for CSH-added sites/customers
  const findMainCustomerKey = (customerId?: number, customerName?: string) => {
    if (!Array.isArray(nodes)) {return undefined;}
    try {
      for (const n of nodes) {
        if (n && (n.customerId != null) && Number(n.customerId) === Number(customerId)) {return n.key;}
        if (customerName && n.title && String(n.title).trim().toLowerCase() === String(customerName).trim().toLowerCase()) {return n.key;}
      }
    } catch (e) { /* ignore */ }
    return undefined;
  };

  const findMainSiteKey = (site: AnyObject) => {
    if (!Array.isArray(nodes)) {return undefined;}
    try {
      for (const cust of nodes) {
        const children = Array.isArray(cust.children) ? cust.children : [];
        for (const ch of children) {
          try {
            const chSir = (ch as AnyObject).sirId ?? (ch as AnyObject).siteRegistryId ?? null;
            const chGuid = (ch as AnyObject).siteGuid ? String((ch as AnyObject).siteGuid).toLowerCase() : null;
            const chIdentifier = (ch as AnyObject).siteIdentifier ? String((ch as AnyObject).siteIdentifier).toLowerCase() : null;
            const sSir = (site as AnyObject).siteIdentifierRegistryId ?? (site as AnyObject).siteRegistryId ?? (site as AnyObject).sirId ?? null;
            const sGuid = (site as AnyObject).siteGuid ? String((site as AnyObject).siteGuid).toLowerCase() : null;
            const sIdentifier = (site as AnyObject).siteIdentifier ? String((site as AnyObject).siteIdentifier).toLowerCase() : null;

            if (sSir && chSir && String(sSir) === String(chSir)) {return ch.key;}
            if (sGuid && chGuid && sGuid === chGuid) {return ch.key;}
            if (sIdentifier && chIdentifier && sIdentifier === chIdentifier) {return ch.key;}
            if (site.siteName && ch.title && String(site.siteName).trim().toLowerCase() === String(ch.title).trim().toLowerCase()) {return ch.key;}
          } catch (e) { /* ignore per-child */ }
        }
      }
    } catch (e) { /* ignore */ }
    return undefined;
  };

  // Calculate total unacknowledged alarms for sound alert
  const totalUnacknowledgedCount = useMemo(() => {
    const sourceArray = Array.isArray(bureauAllData) && bureauAllData.length > 0 ? bureauAllData : bureauData;
    const unacknowledged = Array.isArray(sourceArray) ? sourceArray.filter((alarm: BureauAlarm) => !isAlarmAcknowledged(alarm)) : [];
    
    // Deduplicate by alarm ID to get accurate count
    const seen = new Set<string|number>();
    const uniqueCount = unacknowledged.filter((alarm: BureauAlarm) => {
      const uid = alarm.bureauAlarmId ?? alarm.id ?? alarm.alarmIdentifier ?? `${alarm.siteGuid || ''}-${alarm.lastOccurrenceAt || ''}`;
      if (seen.has(uid)) {return false;}
      seen.add(uid);
      return true;
    }).length;
    
    return uniqueCount;
  }, [bureauData, bureauAllData, isAlarmAcknowledged]);

  // Get mute state from context
  const { isMuted } = useAlarmSoundContext();

  // Sidebar group visibility context
  const { admin: showAdmin, csh: showCSH, alarm: showAlarm, help: showHelp, cshGroupVisibility, addedCsh: showAddedCsh } = useSidebarVisibility();

  // CSH groups state
  const [cshSummaries, setCshSummaries] = useState<CshGroupSummary[] | null>(null);
  const [cshGroupUnacksMap, setCshGroupUnacksMap] = useState<Record<number, number>>({});
  const [cshSidebarSiteCountsMap, setCshSidebarSiteCountsMap] = useState<Record<number, Record<number, number>>>({});
  const [cshDetails, setCshDetails] = useState<Record<number, CshGroupDetail | null>>({});
  const [cshLoading, setCshLoading] = useState<boolean>(false);

  const visibleCshGroupIds = useMemo(() => {
    const summaries = Array.isArray(cshSummaries) ? cshSummaries : [];
    return summaries
      .filter(g => (cshGroupVisibility && Object.prototype.hasOwnProperty.call(cshGroupVisibility, g.id)) ? cshGroupVisibility[g.id] !== false : true)
      .map(g => g.id);
  }, [cshSummaries, cshGroupVisibility]);

  const visibleCshGroupIdsKey = useMemo(() => visibleCshGroupIds.join(','), [visibleCshGroupIds]);

  const applySidebarCounts = useCallback((items: CshSidebarGroup[]) => {
    const groupCounts: Record<number, number> = {};
    const siteCountsByGroup: Record<number, Record<number, number>> = {};
    if (Array.isArray(items)) {
      items.forEach((item) => {
        const gid = Number(item.groupId);
        if (!Number.isFinite(gid)) {return;}
        groupCounts[gid] = Number(item.unacknowledgedCount) || 0;
        const siteCounts: Record<number, number> = {};
        if (Array.isArray(item.siteCounts)) {
          item.siteCounts.forEach((sc) => {
            const sir = Number(sc.siteIdentifierRegistryId);
            if (!Number.isFinite(sir) || sir <= 0) {return;}
            siteCounts[sir] = Number(sc.unacknowledgedCount) || 0;
          });
        }
        siteCountsByGroup[gid] = siteCounts;
      });
    }
    setCshGroupUnacksMap(groupCounts);
    setCshSidebarSiteCountsMap(siteCountsByGroup);
  }, []);

  const refreshCshSidebarSignalRData = useCallback(async () => {
    try {
      refreshCshGroupsTree();

      const unacks = await fetchCshSidebarGroups({
        includeSiteCounts: true,
        groupIds: visibleCshGroupIds,
      }).catch(() => [] as CshSidebarGroup[]);
      applySidebarCounts(unacks);
    } catch (e) {
      // ignore transient SignalR-triggered refresh errors
    }
  }, [refreshCshGroupsTree, visibleCshGroupIds, applySidebarCounts]);

  const cshSignalRRefreshTimerRef = useRef<number | null>(null);
  const scheduleCshSignalRRefresh = useCallback(() => {
    if (cshSignalRRefreshTimerRef.current !== null) {
      window.clearTimeout(cshSignalRRefreshTimerRef.current);
    }
    cshSignalRRefreshTimerRef.current = window.setTimeout(() => {
      cshSignalRRefreshTimerRef.current = null;
      void refreshCshSidebarSignalRData();
    }, 350);
  }, [refreshCshSidebarSignalRData]);

  useEffect(() => {
    return () => {
      if (cshSignalRRefreshTimerRef.current !== null) {
        window.clearTimeout(cshSignalRRefreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setCshLoading(true);
      try {
        const summaries = await fetchCshGroups().catch(() => [] as CshGroupSummary[]);

        if (!mounted) {return;}
        setCshSummaries(Array.isArray(summaries) ? summaries : []);

        const selectedVisibleIds = (Array.isArray(summaries) ? summaries : [])
          .filter(g => (cshGroupVisibility && Object.prototype.hasOwnProperty.call(cshGroupVisibility, g.id)) ? cshGroupVisibility[g.id] !== false : true)
          .map(g => g.id);

        const unacks = await fetchCshSidebarGroups({
          includeSiteCounts: true,
          groupIds: selectedVisibleIds,
        }).catch(() => [] as CshSidebarGroup[]);

        if (!mounted) {return;}
        applySidebarCounts(unacks);

        // Fetch details for each summary (best-effort)
        const detailPromises = (Array.isArray(summaries) ? summaries : []).map(s => fetchCshGroup(s.id).catch(() => null));
        const details = await Promise.all(detailPromises);
        if (!mounted) {return;}
        const detailMap: Record<number, CshGroupDetail | null> = {};
        details.forEach((d: CshGroupDetail | null, i: number) => {
          const id = summaries && summaries[i] ? summaries[i].id : undefined;
          if (id !== undefined) {detailMap[id] = d;}
        });
        // Enrich each detail's sites with Virtual Tenant children where possible
        try {
          const enrichPromises: Array<Promise<void>> = [];
          Object.keys(detailMap).forEach((gid) => {
            const det = detailMap[Number(gid)];
            if (!det || !Array.isArray(det.sites)) {return;}
            det.sites.forEach((s: AnyObject, idx: number) => {
              const siteRegistryId = (s as AnyObject).siteIdentifierRegistryId ?? (s as AnyObject).siteRegistryId ?? (s as AnyObject).sirId ?? null;
              if (!siteRegistryId) {return;}
              // schedule getSite to fetch children (VTs may be present)
              enrichPromises.push((async () => {
                try {
                  const res = await getSite(Number(siteRegistryId)).catch(() => ({ success: false } as unknown as { success: boolean }));
                  if (res && res.success && res.data && Array.isArray((res.data as AnyObject).children)) {
                    const children = ((res.data as AnyObject).children as AnyObject[]);
                    const vts = children.filter((c: AnyObject) => (c as AnyObject).entityType === 'VirtualTenant' || (c as AnyObject).isVirtualTenant === true);
                    if (vts && vts.length > 0) {
                      // attach a virtualTenants array to the site entry
                      (det.sites as AnyObject[])[idx] = { ...(det.sites as AnyObject[])[idx], virtualTenants: vts } as AnyObject;
                    }
                  }
                } catch (e) { /* ignore per-site enrich errors */ }
              })());
            });
          });
          await Promise.allSettled(enrichPromises);
        } catch (e) { /* ignore enrich errors */ }
        setCshDetails(detailMap);
      } catch (e) {
        // ignore fetch errors
      } finally {
        if (mounted) {setCshLoading(false);}
      }
    })();
    return () => { mounted = false; };
  }, [nodes, cshRefreshKey, cshGroupVisibility, applySidebarCounts]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!Array.isArray(cshSummaries)) {return;}
        const unacks = await fetchCshSidebarGroups({
          includeSiteCounts: true,
          groupIds: visibleCshGroupIds,
        }).catch(() => [] as CshSidebarGroup[]);
        if (!mounted) {return;}
        applySidebarCounts(unacks);
      } catch (e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [visibleCshGroupIdsKey, applySidebarCounts, cshSummaries]);

  // Listen for immediate CSH updates (dispatched from management pages) to update avatars without full refetch
  useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent)?.detail;
        if (!detail || detail.groupId === undefined) {return;}
        const gid = Number(detail.groupId);
        setCshDetails(prev => {
          const copy = { ...(prev || {}) } as Record<number, CshGroupDetail | AnyObject | null>;
          const existing = (copy[gid] as AnyObject) || {};
          // set assignedTo to the provided structured users (or empty array)
          (existing as AnyObject).assignedTo = Array.isArray(detail.assignedUsers) ? detail.assignedUsers : (detail.assignedUsers ? [detail.assignedUsers] : []);
          copy[gid] = existing as unknown as CshGroupDetail | AnyObject | null;
          return copy;
        });
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('csh-updated', handler as EventListener);
    return () => { window.removeEventListener('csh-updated', handler as EventListener); };
  }, []);

  // Refresh CSH sidebar counts when a bureau alarm notification arrives
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      scheduleCshSignalRRefresh();
    });
    return unsubscribe;
  }, [subscribe, scheduleCshSignalRRefresh]);

  // Listen for alarm acknowledgment events and refresh bureau + CSH data
  useEffect(() => {
    const unsubscribe = alarmAcknowledgedEvent.subscribe((payload) => {
      if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
        return;
      }
      refreshBureauData();
      scheduleCshSignalRRefresh();
    });
    return unsubscribe;
  }, [refreshBureauData, scheduleCshSignalRRefresh]);

  // Play sound alert every minute when there are unacknowledged alarms
  useAlarmSound(totalUnacknowledgedCount, 60000, isMuted);

  // Render
  return (
    <aside className={`sidebar ${displayCollapsed ? 'collapsed' : ''}`} ref={sidebarRef}>
      <nav className="sidebar-nav">
        {/* Admin Section */}
        {isAdmin() && !isSupport() && showAdmin && (
          <div className="sidebar-section" >
            <h5>{!displayCollapsed && 'Admin'}</h5>
            <ul className="sidebar-tree__list">
              <li className="sidebar-tree__item sidebar-tree__item--level-0">
                <button 
                  role="treeitem"  
                  ref={usersButtonRef}
                  className="sidebar-tree__button sidebar-tree__button--users" 
                  data-node-id="admin-users"
                  onClick={() => navigate('/admin')}
                  onMouseEnter={handleUsersMouseEnter}
                  onMouseLeave={handleUsersMouseLeave}
                  onFocus={handleUsersMouseEnter}
                  onBlur={handleUsersMouseLeave}
                >
                  <span className="sidebar-tree__title">Users</span>
                </button>
                {displayCollapsed && showUsersTooltip && (
                  <div
                    className="sidebar-tree__tooltip"
                    style={{
                      position: 'fixed',
                      top: `${usersTooltipPosition.top}px`,
                      ...(usersTooltipPosition.left !== undefined ? { left: `${usersTooltipPosition.left}px` } : {}),
                      ...(usersTooltipPosition.right !== undefined ? { right: `${usersTooltipPosition.right}px` } : {}),
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      pointerEvents: 'none'
                    }}
                  >
                    Users
                  </div>
                )}
              </li>
              <li className="sidebar-tree__item sidebar-tree__item--level-0">
                <button
                  role="treeitem"
                  ref={accessButtonRef}
                  className="sidebar-tree__button sidebar-tree__button--access"
                  data-node-id="admin-access-requests"
                  onClick={() => navigate('/admin/request')}
                  onMouseEnter={handleAccessMouseEnter}
                  onMouseLeave={handleAccessMouseLeave}
                  onFocus={handleAccessMouseEnter}
                  onBlur={handleAccessMouseLeave}
                >
                  <span className="sidebar-tree__title">Access Requests</span>
                </button>
                {displayCollapsed && showAccessTooltip && (
                  <div
                    className="sidebar-tree__tooltip"
                    style={{
                      position: 'fixed',
                      top: `${accessTooltipPosition.top}px`,
                      ...(accessTooltipPosition.left !== undefined ? { left: `${accessTooltipPosition.left}px` } : {}),
                      ...(accessTooltipPosition.right !== undefined ? { right: `${accessTooltipPosition.right}px` } : {}),
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      pointerEvents: 'none'
                    }}
                  >
                    Access Requests
                  </div>
                )}
              </li>
              <li className="sidebar-tree__item sidebar-tree__item--level-0">
                <button
                  role="treeitem"
                  ref={adminButtonRef}
                  className="sidebar-tree__button sidebar-tree__button--activity"
                  data-node-id="admin-user-activity"
                  onClick={() => navigate('/admin/user-activity')}
                  onMouseEnter={handleAdminMouseEnter}
                  onMouseLeave={() => setShowAdminTooltip(false)}
                  onFocus={handleAdminMouseEnter}
                  onBlur={() => setShowAdminTooltip(false)}
                >
                  <span className="sidebar-tree__title">User activity</span>
                </button>
                {displayCollapsed && showAdminTooltip && (
                  <div
                    className="sidebar-tree__tooltip"
                    style={{
                      position: 'fixed',
                      top: `${adminTooltipPosition.top}px`,
                      ...(adminTooltipPosition.left !== undefined ? { left: `${adminTooltipPosition.left}px` } : {}),
                      ...(adminTooltipPosition.right !== undefined ? { right: `${adminTooltipPosition.right}px` } : {}),
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      pointerEvents: 'none'
                    }}
                  >
                    User activity
                  </div>
                )}
              </li>
            </ul>
          </div>
        )}

        {/* CSH Groups Section */}
        {!isNormalUser() && shouldFilterForBureau && showCSH && (
          <div className="sidebar-section" key="csh-groups-section">
            <h5>{!displayCollapsed && 'CSH Groups'}</h5>
            <div className="sidebar-tree-wrapper">
                {cshGroupsLoading || cshGroupsMappingInProgress ? (
                  <div className="tree-loading">Loading CSH sites...</div>
                ) : cshGroupsError ? (
                  <div className="tree-error">Failed to load CSH Groups</div>
                ) : cshGroupsNodes && cshGroupsNodes.length > 0 ? (
                  <SidebarTree
                    key="csh-groups-tree"
                    collapsed={displayCollapsed}
                    nodes={cshGroupsNodes}
                    allowedSiteIdentifiers={undefined}
                    allowedTopLevelKeys={undefined}
                    allowedChildrenMap={undefined}
                    unacknowledgedCounts={cshGroupsUnacknowledgedCounts}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    expandedKeys={cshGroupsExpandedKeys}
                    setExpandedKeys={setCshGroupsExpandedKeys}
                    onSelect={(node) => {
                        try { setSelectedId(String(node.key)); } catch (e) { /* ignore */ }
                        const id = (node as AnyObject).sirId ?? (node as AnyObject).key;
                        const storageKey = `site:${id}:filters`;
                        try {
                          const raw = localStorage.getItem(storageKey);
                          const stored = raw ? JSON.parse(raw) : null;
                          const params = new URLSearchParams();
                          if (stored?.cat && stored.cat !== 'all') {params.set('cat', stored.cat);}
                          if (stored?.q) {params.set('q', stored.q);}
                          const suffix = params.toString() ? `?${params.toString()}` : '';
                          navigate(`/sites/${encodeURIComponent(String(id))}${suffix}`);
                        } catch (e) {
                          navigate(`/sites/${encodeURIComponent(String(id))}`);
                        }
                      }}
                  />
                ) : null}
              </div>
            </div>
        )}

        {/* Adding CSH Groups (independent from CSH Groups above) */}
        {showAddedCsh && (
          <div className="sidebar-section" key="added-csh-groups-section">
            <ul className="sidebar-tree__list">
              {cshLoading && <li className="sidebar-tree__item sidebar-tree__item--level-0"><div className="tree-loading">Loading groups...</div></li>}
              {!cshLoading && cshSummaries && cshSummaries.length === 0 && (
                <li className="sidebar-tree__item sidebar-tree__item--level-0"><div className="tree-empty"></div></li>
              )}
              {!cshLoading && cshSummaries && cshSummaries
                .filter(g => (cshGroupVisibility && Object.prototype.hasOwnProperty.call(cshGroupVisibility, g.id)) ? cshGroupVisibility[g.id] !== false : true)
                .map(group => {
                const detail = cshDetails[group.id];
                const sitesMap: Record<string, AnyObject> = {};
                if (detail && Array.isArray(detail.sites)) {
                  for (const s of detail.sites) {
                    const generateFallbackId = () => {
                      try {
                        if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
                          return (crypto as any).randomUUID();
                        }
                        if (typeof crypto !== 'undefined' && typeof (crypto as any).getRandomValues === 'function') {
                          const arr = new Uint8Array(8);
                          (crypto as any).getRandomValues(arr);
                          return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
                        }
                      } catch (e) { /* ignore and fallback */ }
                      return `fallback-${Date.now()}`;
                    };

                    const baseSiteId = s.siteIdentifierRegistryId ?? s.siteGuid ?? generateFallbackId();
                    const siteKey = `csh-added-${group.id}-site-${baseSiteId}`;
                    const siteNode = {
                      key: siteKey,
                      title: s.siteName || s.siteIdentifier || 'Unknown site',
                      type: 'site',
                      siteIdentifier: s.siteIdentifier ?? null,
                      siteGuid: s.siteGuid ?? null,
                      sirId: s.siteIdentifierRegistryId ?? null,
                      shId: s.siteHierarchyId ?? null,
                      children: undefined as AnyObject[] | undefined,
                      childrenCount: 0,
                    };

                    // Find existing site node (so we group multiple VT entries under same site)
                    let existingSite = sitesMap[siteKey];
                    if (!existingSite) {
                      existingSite = { ...siteNode, children: [] as AnyObject[], childrenCount: 0 };
                      sitesMap[siteKey] = existingSite;
                    }

                    // If the enrichment attached a `virtualTenants` array to this site, render each VT
                    const enrichedVts = Array.isArray((s as AnyObject).virtualTenants) ? (s as AnyObject).virtualTenants as AnyObject[] : null;
                    if (enrichedVts && enrichedVts.length > 0) {
                      for (const vt of enrichedVts) {
                        const vtId = (vt && (vt.id ?? vt.vtId ?? vt.siteIdentifier ?? vt.siteGuid)) ?? JSON.stringify(vt);
                        const vtKey = `csh-added-${group.id}-site-${baseSiteId}-vt-${vtId}`;
                        let vtNode = (existingSite.children as AnyObject[]).find((c: AnyObject) => (c as AnyObject).key === vtKey && (c as AnyObject).type === 'vt');
                        if (!vtNode) {
                          vtNode = {
                            key: vtKey,
                            title: vt.name || vt.siteIdentifier || `VT ${vtId}`,
                            type: 'vt',
                            children: [] as AnyObject[],
                            childrenCount: 0,
                          };
                          existingSite.children.push(vtNode);
                        }
                        // Do not duplicate the site under the VT — make VT a leaf node
                        vtNode.children = undefined;
                        vtNode.childrenCount = 0;
                      }
                      existingSite.childrenCount = existingSite.children.length;
                    } else {
                      // legacy single-VT fields (fall back)
                      const vtId = (s as AnyObject).virtualTenantId ?? (s as AnyObject).vtId ?? (s as AnyObject).virtualTenantGuid ?? null;
                      const vtName = (s as AnyObject).virtualTenantName ?? (s as AnyObject).vtName ?? null;
                      if (vtId) {
                        const vtKey = `csh-added-${group.id}-site-${baseSiteId}-vt-${vtId}`;
                        let vtNode = (existingSite.children as AnyObject[]).find((c: AnyObject) => (c as AnyObject).key === vtKey && (c as AnyObject).type === 'vt');
                        if (!vtNode) {
                          vtNode = {
                            key: vtKey,
                            title: vtName || `VT ${vtId}`,
                            type: 'vt',
                            children: [] as AnyObject[],
                            childrenCount: 0,
                          };
                          existingSite.children.push(vtNode);
                        }
                        // Make VT a leaf node (avoid duplicating the site beneath it)
                        vtNode.children = undefined;
                        vtNode.childrenCount = 0;
                        existingSite.childrenCount = existingSite.children.length;
                      } else {
                        // non-VT site - ensure not duplicated
                        existingSite.childrenCount = existingSite.children.length;
                      }
                    }
                  }
                }
                const customerNodes = Object.values(sitesMap) as AnyObject[];
                for (const site of customerNodes) {
                  site.childrenCount = (site.children && site.children.length) || 0;
                }
                const detailObj = (cshDetails[group.id] as AnyObject | undefined) ?? undefined;
                const usersFromDetail = Array.isArray(detailObj?.users) ? (detailObj?.users as AnyObject[]) : [];
                const usersFromAssigned = Array.isArray(detailObj?.assignedTo) ? (detailObj?.assignedTo as AnyObject[]) : [];
                const sourceUsers = usersFromDetail.length > 0 ? usersFromDetail : usersFromAssigned;

                const toDisplayName = (u: AnyObject): string => {
                  if (!u) {return '';}
                  if (typeof u === 'string') {return u.trim();}

                  const first = String((u as AnyObject).firstName ?? '').trim();
                  const last = String((u as AnyObject).lastName ?? '').trim();
                  const full = `${first} ${last}`.trim();
                  if (full) {return full;}

                  const label = String((u as AnyObject).label ?? '').trim();
                  if (label) {return label;}

                  const email = String((u as AnyObject).email ?? '').trim();
                  if (email) {return email;}

                  const employeeId = String((u as AnyObject).employeeId ?? '').trim();
                  if (employeeId) {return employeeId;}

                  return '';
                };

                const userNames = Array.from(
                  new Set(
                    sourceUsers
                      .map((u: AnyObject) => toDisplayName(u))
                      .filter(Boolean)
                  )
                );

                const getInitials = (name: string): string => {
                  const parts = String(name || '')
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);
                  if (parts.length === 0) {return '?';}
                  if (parts.length === 1) {
                    return parts[0].slice(0, 2).toUpperCase();
                  }
                  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
                };

                const getAvatarStyle = (name: string): React.CSSProperties => {
                  let hash = 0;
                  const text = String(name || '');
                  for (let i = 0; i < text.length; i += 1) {
                    hash = ((hash << 5) - hash) + text.charCodeAt(i);
                    hash |= 0;
                  }
                  const hue = Math.abs(hash) % 360;
                  return {
                    backgroundColor: `hsl(${hue} 65% 45%)`,
                    color: '#ffffff',
                  };
                };

                const visibleUserNames = userNames.slice(0, 2);
                const overflowUsersCount = Math.max(userNames.length - visibleUserNames.length, 0);
                const assigneeSummary = userNames.length > 0 ? userNames.join(', ') : 'No users assigned';

                return (
                  <li className="sidebar-tree__item sidebar-tree__item--level-0" key={`added-csh-group-${group.id}`}>
                    <div
                      className="csh-group__header"
                      role="button"
                      tabIndex={0}
                      aria-label={`CSH group ${group.name}`}
                      aria-current={location.pathname === `/csh-details/${group.id}` ? 'page' : undefined}
                      onClick={() => navigate(`/csh-details/${encodeURIComponent(String(group.id))}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/csh-details/${encodeURIComponent(String(group.id))}`);
                        }
                      }}
                    >
                      {!displayCollapsed && (
                        <>
                          <div className="overflow-count">
                            {overflowUsersCount > 0 && (
                              <div title={`${overflowUsersCount} more`}>+{overflowUsersCount}</div>
                            )}
                          </div>
                          <h5 className="csh-group__title">{group.name}</h5>
                          <div className="assignees">
                            {visibleUserNames.length > 0 ? (
                              <>
                                {visibleUserNames.map((name) => (
                                  <div
                                    key={`${group.id}-${name}`}
                                    title={assigneeSummary}
                                    className="sidebar--avatar"
                                    style={getAvatarStyle(name)}
                                  >
                                    {getInitials(name)}
                                  </div>
                                ))}
                              </>
                            ) : (
                              <span className="csh-group__assignees-empty">No users assigned</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                      <div id={`added-csh-tree-${group.id}`} className="csh-group__tree">
                            {customerNodes && customerNodes.length > 0 ? (
                          (() => {
                            // Build unacknowledged counts from sidebar siteCounts (authoritative, lightweight).
                            const mappedCounts: Record<string, number> = {};
                            try {
                              const sidebarSiteCounts = cshSidebarSiteCountsMap[group.id] || {};
                              if (Array.isArray(customerNodes)) {
                                // customerNodes is now site nodes directly — no customer level
                                const sirToSiteKey: Record<string, string> = {};
                                const guidToSiteKey: Record<string, string> = {};
                                const identToSiteKey: Record<string, string> = {};
                                for (const s of customerNodes) {
                                  const siteKey = String((s as AnyObject).key);
                                  const sir = (s as AnyObject).sirId;
                                  const guid = (s as AnyObject).siteGuid;
                                  const ident = (s as AnyObject).siteIdentifier;
                                  if (sir) {sirToSiteKey[String(sir)] = siteKey;}
                                  if (guid) {guidToSiteKey[String(guid).toLowerCase()] = siteKey;}
                                  if (ident) {identToSiteKey[String(ident).toLowerCase()] = siteKey;}
                                }
                                Object.keys(sidebarSiteCounts).forEach((sir) => {
                                  const siteKey = sirToSiteKey[String(sir)];
                                  const count = Number(sidebarSiteCounts[Number(sir)] ?? 0) || 0;
                                  if (siteKey && count > 0) {
                                    mappedCounts[siteKey] = count;
                                  }
                                });

                              }
                            } catch (e) {
                               console.info('Failed to map unacknowledged counts for CSH-added group', { groupId: group.id, error: e });
                             }

                            return (
                              <SidebarTree
                                key={`added-csh-tree-${group.id}`}
                                collapsed={displayCollapsed}
                                nodes={customerNodes}
                                allowedSiteIdentifiers={undefined}
                                allowedTopLevelKeys={undefined}
                                allowedChildrenMap={undefined}
                                unacknowledgedCounts={mappedCounts}
                                selectedId={selectedId}
                                setSelectedId={setSelectedId}
                                startLevel={1}
                                onSelect={(node) => {
                                try { setSelectedId(String((node as AnyObject).key)); } catch (e) { /* ignore */ }
                                const id = (node as AnyObject).sirId ?? (node as AnyObject).key;
                                const storageKey = `site:${id}:filters`;
                                try {
                                  const raw = localStorage.getItem(storageKey);
                                  const stored = raw ? JSON.parse(raw) : null;
                                  const params = new URLSearchParams();
                                  if (stored?.cat && stored.cat !== 'all') {params.set('cat', stored.cat);}
                                  if (stored?.q) {params.set('q', stored.q);}
                                  const suffix = params.toString() ? `?${params.toString()}` : '';
                                  navigate(`/sites/${encodeURIComponent(String(id))}${suffix}`);
                                } catch (e) {
                                  navigate(`/sites/${encodeURIComponent(String(id))}`);
                                }
                              }}
                              />
                          );
                          })()
                        ) : (
                          <div className="tree-empty"></div>
                        )}
                      </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Alarm Groups Section */}
        {showAlarm && (
          <div className="sidebar-section" key="alarm-groups-section">
              <h5>{!displayCollapsed && 'Alarm Groups'}</h5>
            <div className="sidebar-tree-wrapper">
            {treeLoading && <div className="tree-loading">Loading sites...</div>}
            {treeError && <div className="tree-error">{treeError}</div>}
            {!treeLoading && !treeError && nodes && (
              <SidebarTree
                key="alarm-groups-tree"
                collapsed={displayCollapsed}
                nodes={nodes}
                allowedSiteIdentifiers={undefined}
                allowedTopLevelKeys={undefined}
                allowedChildrenMap={undefined}
                unacknowledgedCounts={unacknowledgedCounts}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                onSelect={(node) => {
                  try { setSelectedId(String(node.key)); } catch (e) { 
                    console.info('Failed to set selectedId in sidebar on node select', { node, error: e });
                   }
                  const id = (node as AnyObject).sirId ?? (node as AnyObject).key;
                  const storageKey = `site:${id}:filters`;
                  try {
                    const raw = localStorage.getItem(storageKey);
                    const stored = raw ? JSON.parse(raw) : null;
                    const params = new URLSearchParams();
                    if (stored?.cat && stored.cat !== 'all') {params.set('cat', stored.cat);}
                    if (stored?.q) {params.set('q', stored.q);}
                    const suffix = params.toString() ? `?${params.toString()}` : '';
                    navigate(`/sites/${encodeURIComponent(String(id))}${suffix}`);
                  } catch (e) {
                    navigate(`/sites/${encodeURIComponent(String(id))}`);
                  }
                }}
              />
            )}
            </div>
          </div>
        )}

         {/* Help Section for Normal users */}
        {(isNormalUser() || isAdmin()) && showHelp && (
          <div className="sidebar-section" >
            <h5>{!displayCollapsed && 'Help'}</h5>
            <ul className="sidebar-tree__list">
              <li className="sidebar-tree__item sidebar-tree__item--level-0">
                <button 
                  role="treeitem"  
                  ref={helpButtonRef}
                  className="sidebar-tree__button sidebar-tree__button--help" 
                  data-node-id="help-main"
                  onClick={() => navigate('/help')}
                  onMouseEnter={handleHelpMouseEnter}
                  onMouseLeave={handleHelpMouseLeave}
                  onFocus={handleHelpMouseEnter}
                  onBlur={handleHelpMouseLeave}
                >
                  <span className="sidebar-tree__title">Help</span>
                </button>
                {displayCollapsed && showHelpTooltip && (
                  <div
                    className="sidebar-tree__tooltip"
                    style={{
                      position: 'fixed',
                      top: `${helpTooltipPosition.top}px`,
                      ...(helpTooltipPosition.left !== undefined ? { left: `${helpTooltipPosition.left}px` } : {}),
                      ...(helpTooltipPosition.right !== undefined ? { right: `${helpTooltipPosition.right}px` } : {}),
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      pointerEvents: 'none'
                    }}
                  >
                    Help
                  </div>
                )}
              </li>
              <li className="sidebar-tree__item sidebar-tree__item--level-0">
                <button
                  role="treeitem"
                  ref={accessButtonRef}
                  className="sidebar-tree__button sidebar-tree__button--access"
                  data-node-id="help-access"
                  onClick={() => navigate('/request-access')}
                  onMouseEnter={handleAccessMouseEnter}
                  onMouseLeave={handleAccessMouseLeave}
                  onFocus={handleAccessMouseEnter}
                  onBlur={handleAccessMouseLeave}
                >
                  <span className="sidebar-tree__title">Request Access</span>
                </button>
                {displayCollapsed && showAccessTooltip && (
                  <div
                    className="sidebar-tree__tooltip"
                    style={{
                      position: 'fixed',
                      top: `${accessTooltipPosition.top}px`,
                      left: `${accessTooltipPosition.left}px`,
                      transform: 'translateY(-50%)',
                      zIndex: 9999,
                      pointerEvents: 'none'
                    }}
                  >
                    Request Access
                  </div>
                )}
              </li>
            </ul>
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
